import React, { useState, useEffect } from 'react';
import { 
  ChatBubbleOvalLeftEllipsisIcon, 
  TrashIcon, 
  EnvelopeOpenIcon,
  StarIcon as StarOutline,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import { 
  getAllMessages, 
  markMessageAsRead, 
  deleteMessage, 
  Message,
  updateMessageImportance,
  updateMessageRepliedStatus
} from '../../firebase/messageService';

const MessagesView: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'all' | 'unread' | 'important' | 'archived'>('all');
  const [messageDetailsId, setMessageDetailsId] = useState<string | null>(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const fetchedMessages = await getAllMessages();
      setMessages(fetchedMessages);
    } catch (error) {
      toast.error('Failed to load messages');
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await markMessageAsRead(id);
      setMessages(messages.map(msg => 
        msg.id === id ? { ...msg, read: true } : msg
      ));
      toast.success('Message marked as read');
    } catch (error) {
      toast.error('Failed to mark message as read');
      console.error('Error marking message as read:', error);
    }
  };

  const handleToggleImportance = async (id: string, currentValue: boolean = false) => {
    try {
      await updateMessageImportance(id, !currentValue);
      setMessages(messages.map(msg => 
        msg.id === id ? { ...msg, important: !currentValue } : msg
      ));
      toast.success(currentValue ? 'Removed from important' : 'Marked as important');
    } catch (error) {
      toast.error('Failed to update message importance');
      console.error('Error updating message importance:', error);
    }
  };

  const handleToggleReplied = async (id: string, currentValue: boolean = false) => {
    try {
      await updateMessageRepliedStatus(id, !currentValue);
      setMessages(messages.map(msg => 
        msg.id === id ? { ...msg, replied: !currentValue } : msg
      ));
      toast.success(currentValue ? 'Marked as not replied' : 'Marked as replied');
    } catch (error) {
      toast.error('Failed to update reply status');
      console.error('Error updating reply status:', error);
    }
  };

  const handleDeleteMessage = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await deleteMessage(id);
        setMessages(messages.filter(msg => msg.id !== id));
        if (messageDetailsId === id) {
          setMessageDetailsId(null);
        }
        toast.success('Message deleted successfully');
      } catch (error) {
        toast.error('Failed to delete message');
        console.error('Error deleting message:', error);
      }
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    if (timestamp.toDate) {
      const date = timestamp.toDate();
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      }).format(date);
    }
    
    return new Date(timestamp).toLocaleString();
  };

  const filteredMessages = messages.filter(message => {
    switch (selectedTab) {
      case 'unread':
        return !message.read;
      case 'important':
        return message.important;
      case 'archived':
        return message.archived;
      default:
        return true;
    }
  });

  const selectedMessage = messageDetailsId 
    ? messages.find(msg => msg.id === messageDetailsId) 
    : null;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Messages</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-4">
        View and manage contact messages from visitors.
      </p>

      {/* Tabs */}
      <div className="mb-6 flex space-x-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setSelectedTab('all')}
          className={`px-4 py-2 font-medium ${
            selectedTab === 'all'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setSelectedTab('unread')}
          className={`px-4 py-2 font-medium ${
            selectedTab === 'unread'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          Unread
        </button>
        <button
          onClick={() => setSelectedTab('important')}
          className={`px-4 py-2 font-medium ${
            selectedTab === 'important'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          Important
        </button>
        <button
          onClick={() => setSelectedTab('archived')}
          className={`px-4 py-2 font-medium ${
            selectedTab === 'archived'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          Archived
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 h-[calc(100vh-240px)] overflow-y-auto">
          {filteredMessages.length > 0 ? (
            <div className="space-y-3">
              {filteredMessages.map((message) => (
                <div
                  key={message.id}
                  onClick={() => {
                    if (message.id) {
                      setMessageDetailsId(message.id);
                      if (!message.read) {
                        handleMarkAsRead(message.id);
                      }
                    }
                  }}
                  className={`p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    messageDetailsId === message.id
                      ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/10'
                      : !message.read
                      ? 'border-gray-300 dark:border-gray-600 bg-blue-50 dark:bg-blue-900/10'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium flex items-center">
                        {message.name}
                        {message.important && (
                          <StarSolid className="h-4 w-4 text-yellow-500 ml-1" />
                        )}
                        {!message.read && (
                          <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full inline-block"></span>
                        )}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {message.email}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(message.createdAt)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-medium truncate">
                    {message.subject}
                  </p>
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[70%]">
                      {message.message}
                    </p>
                    {message.replied && (
                      <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                        Replied
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <ChatBubbleOvalLeftEllipsisIcon className="h-12 w-12 mb-3" />
              <p className="text-center">No messages found</p>
            </div>
          )}
        </div>

        {/* Message Details */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 h-[calc(100vh-240px)] overflow-y-auto">
          {selectedMessage ? (
            <div>
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold">{selectedMessage.subject}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    From: {selectedMessage.name} ({selectedMessage.email})
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Received: {formatDate(selectedMessage.createdAt)}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => selectedMessage.id && handleToggleImportance(selectedMessage.id, selectedMessage.important)}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label={selectedMessage.important ? 'Remove from important' : 'Mark as important'}
                  >
                    {selectedMessage.important ? (
                      <StarSolid className="h-5 w-5 text-yellow-500" />
                    ) : (
                      <StarOutline className="h-5 w-5 text-gray-400 hover:text-yellow-500" />
                    )}
                  </button>
                  <button
                    onClick={() => selectedMessage.id && handleToggleReplied(selectedMessage.id, selectedMessage.replied)}
                    className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      selectedMessage.replied ? 'text-green-600' : 'text-gray-400 hover:text-green-600'
                    }`}
                    aria-label={selectedMessage.replied ? 'Mark as not replied' : 'Mark as replied'}
                  >
                    <EnvelopeOpenIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => selectedMessage.id && handleDeleteMessage(selectedMessage.id)}
                    className="p-2 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="Delete message"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg whitespace-pre-wrap">
                {selectedMessage.message}
              </div>
              
              <div className="mt-6 flex justify-between">
                <div>
                  {!selectedMessage.read && selectedMessage.id && (
                    <button
                      onClick={() => handleMarkAsRead(selectedMessage.id as string)}
                      className="px-4 py-2 text-sm bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 rounded-md hover:bg-blue-200 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      Mark as read
                    </button>
                  )}
                </div>
                <div>
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                    className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    onClick={() => {
                      if (selectedMessage.id && !selectedMessage.replied) {
                        handleToggleReplied(selectedMessage.id, false);
                      }
                    }}
                  >
                    Reply
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <ChatBubbleOvalLeftEllipsisIcon className="h-16 w-16 mb-4" />
              <p className="text-lg">Select a message to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesView; 